import React from 'react'
import { Router, Switch, Route, Redirect } from 'react-router-dom'
import { Subject } from 'rxjs'
import { filter, map, share } from 'rxjs/operators'
import { OrderedSet } from 'immutable'
import qs from 'query-string'

import { openTransientModal$ } from '@apiEnabler/api-enabler-common/lib/intents'
import { history, flog, createStateContext } from '@zambezi/caballo-vivo'
import { domSink, redirectSink } from '@apiEnabler/api-enabler-common/lib/sink'
import { hasRequiredPropsForRender } from '@apiEnabler/api-enabler-common/lib/view-utils'

import { startLogOut$ } from '@apiEnabler/ob-common/lib/intents'
import Loader from '@apiEnabler/ob-common/lib/view/Loader'
import Footer from '@apiEnabler/ob-common/lib/view/Footer'
import ScrollToTop from '@apiEnabler/ob-common/lib/view/ScrollToTop'

import {
  cancelAccess$,
  cancelFundsCheck$,
  cancelVrp$,
  cancelSweeping$,
  cancelLink$,
} from './intents'
import { canCancelConsent } from './permissions'
import { isLegacyTheme } from '@apiEnabler/ob-common/lib/brand'

import AccessAgreement from './view/AccessAgreement'
import Accesses from './view/Accesses'
import AccessTPPs from './view/AccessTPPs'
import Biometrics from './view/Biometrics'
import Error from './view/Errors'
import FundsChecks from './view/FundsChecks'
import FundsCheckTpps from './view/FundsCheckTpps'
import FundsCheckOverview from './view/FundsCheckOverview'
import Masthead from './view/Masthead'
import Modal from './view/Modals'
import Mortgages from './view/Mortgages'
import Overview from './view/Overview'
import Payment from './view/Payment'
import Payments from './view/Payments'
import Privacy from './Privacy'
import RecurringPayments from './view/RecurringPayments'
import StandingOrder from './view/StandingOrder'
import SweepingPayment from './view/SweepingPayment'
import SweepingPayments from './view/SweepingPayments'
import VrpPayment from './view/VrpPayment'
import VrpPayments from './view/VrpPayments'
import PayMeYou from './view/PayMeYou'
import LinkHistory from './view/LinkHistory'

import style from './view.module.css'

const render$ = new Subject().pipe(share())

function toView(state) {
  const rootPath = state.get('rootPath')
  const brand = state.get('brand')
  const consentCancelEnabled = canCancelConsent(state.get('employee'))
  const isStaffMember = state.get('isStaffMember')
  const showMortgageConsents =
    isStaffMember || brand.get('showMortgageConsents')
  const isPayMeYouEnabled = state.get('isPayMeYouEnabled')

  return (
    <Router history={history}>
      <ScrollToTop />
      <div className="ob-page zb-flex zb-flex-column">
        <Masthead
          logout={() => startLogOut$.next()}
          hasError={state.has('error')}
          customer={state.get('customer')}
          authToken={state.get('authToken')}
          isStaffMember={isStaffMember}
          brandLogo={state.getIn(['brand', 'brandLogo'])}
          openNavMenu={() =>
            openTransientModal$.next({
              type: 'nav-mobile',
            })
          }
          isLegacyTheme={isLegacyTheme(brand.get('id'))}
        />
        <main className={`${style.container} ${style.content}`}>
          {state.has('error') ? (
            <Error
              error={state.get('error')}
              brand={brand}
              isStaffMember={isStaffMember}
            />
          ) : state.get('loading', OrderedSet()).size ? (
            <Loader text={state.get('loading').last()} />
          ) : (
            <Switch location={state.get('location').toJS()}>
              {/* Homepage routes */}
              <Redirect exact from="/" to={rootPath} />
              <Route
                exact
                path={rootPath}
                render={() => (
                  <Overview
                    isStaffMember={isStaffMember}
                    showMortgageConsents={showMortgageConsents}
                    isPayMeYouEnabled={state.get('isPayMeYouEnabled')}
                  />
                )}
              />

              {/* Access Agreements routes */}
              <Redirect
                exact
                from="/access-agreements"
                to={`${rootPath}/access-agreements/tpps`}
              />
              <Redirect
                exact
                from="/access-agreements/tpps"
                to={`${rootPath}/access-agreements/tpps`}
              />
              <Redirect
                exact
                from="/access-agreements/tpps/:clientId"
                to={`${rootPath}/access-agreements/tpps/:clientId/agreements`}
              />
              <Redirect
                exact
                from="/access-agreements/tpps/:clientId/agreements"
                to={`${rootPath}/access-agreements/tpps/:clientId/agreements`}
              />
              <Redirect
                exact
                from="/access-agreements/tpps/:clientId/agreements/:accessId"
                to={`${rootPath}/access-agreements/tpps/:clientId/agreements/:accessId`}
              />
              <Route
                exact
                path={`${rootPath}/access-agreements/tpps`}
                render={() => {
                  const handleChangesTo90DayDetails = () => {
                    openTransientModal$.next({
                      type: 'changes-90-day-auth-access.detail',
                    })
                  }
                  return (
                    <AccessTPPs
                      accesses={state.get('accesses')}
                      handleDisaplyChangesTo90DayDetails={
                        handleChangesTo90DayDetails
                      }
                    />
                  )
                }}
              />
              <Route
                exact
                path={`${rootPath}/access-agreements/tpps/:clientId/agreements`}
                render={({ match }) => {
                  const { clientId } = match.params
                  const tpp = state.getIn(['accesses', 'tpps', clientId])
                  const handle = () => {
                    openTransientModal$.next({
                      type: 'access-status-meaning.detail',
                    })
                  }
                  return (
                    <Accesses tpp={tpp} handleDisplayStatusesMeaning={handle} />
                  )
                }}
              />
              <Route
                exact
                path={`${rootPath}/access-agreements/tpps/:clientId/agreements/:accessId`}
                render={({ match }) => (
                  <AccessAgreement
                    tpp={state.getIn([
                      'accesses',
                      'tpps',
                      match.params.clientId,
                    ])}
                    access={state.getIn([
                      'accesses',
                      'index',
                      match.params.accessId,
                    ])}
                    onCancelAccess={
                      isStaffMember &&
                      state.getIn([
                        'accesses',
                        'index',
                        match.params.accessId,
                        'status',
                      ]) === 'ACCEPTED'
                        ? null
                        : consentCancelEnabled
                        ? access => cancelAccess$.next(access)
                        : null
                    }
                    isStaffMember={isStaffMember}
                    handleShowContinueAccess={() =>
                      openTransientModal$.next({
                        type: 'continue-access-info.show',
                      })
                    }
                    accountNumberFormat={state.getIn([
                      'brand',
                      'accountNumberFormat',
                    ])}
                  />
                )}
              />

              {/* Funds Checks routes */}
              <Redirect
                exact
                from="/funds-checks"
                to={`${rootPath}/funds-checks/tpps`}
              />
              <Redirect
                exact
                from="/funds-checks/tpps"
                to={`${rootPath}/funds-checks/tpps`}
              />
              <Redirect
                exact
                from="/funds-checks/tpps/:clientId"
                to={`${rootPath}/funds-checks/tpps/:clientId/funds-checks`}
              />
              <Redirect
                exact
                from="/funds-checks/tpps/:clientId/funds-checks"
                to={`${rootPath}/funds-checks/tpps/:clientId/funds-checks`}
              />
              <Redirect
                exact
                from="/funds-checks/tpps/:clientId/funds-checks/:fundsCheckId"
                to={`${rootPath}/funds-checks/tpps/:clientId/funds-checks/:fundsCheckId`}
              />
              <Route
                exact
                path={`${rootPath}/funds-checks/tpps`}
                render={() => (
                  <FundsCheckTpps fundsChecks={state.get('fundsChecks')} />
                )}
              />
              <Route
                exact
                path={`${rootPath}/funds-checks/tpps/:clientId/funds-checks`}
                render={({ match }) => {
                  const { clientId } = match.params
                  const tpp = state.getIn(['fundsChecks', 'tpps', clientId])
                  const handle = () => {
                    openTransientModal$.next({
                      type: 'funds-check-status-meaning.detail',
                    })
                  }
                  return (
                    <FundsChecks
                      tpp={tpp}
                      handleDisplayStatusesMeaning={handle}
                    />
                  )
                }}
              />
              <Route
                exact
                path={`${rootPath}/funds-checks/tpps/:clientId/funds-checks/:fundsCheckId`}
                render={({ match }) => (
                  <FundsCheckOverview
                    tpp={state.getIn([
                      'fundsChecks',
                      'tpps',
                      match.params.clientId,
                    ])}
                    fundsCheck={state.getIn([
                      'fundsChecks',
                      'index',
                      match.params.fundsCheckId,
                    ])}
                    isStaffMember={isStaffMember}
                    onCancel={
                      isStaffMember &&
                      state.getIn([
                        'fundsChecks',
                        'index',
                        match.params.fundsCheckId,
                        'status',
                      ]) === 'ACCEPTED'
                        ? null
                        : consentCancelEnabled
                        ? fundsCheck => cancelFundsCheck$.next(fundsCheck)
                        : null
                    }
                  />
                )}
              />

              {/* Payments routes */}
              <Redirect exact from="/payments" to={`${rootPath}/payments`} />
              <Redirect
                exact
                from="/payments/:paymentId"
                to={`${rootPath}/payments/:paymentId`}
              />
              <Route
                exact
                path={`${rootPath}/payments`}
                render={({ location }) => (
                  <Payments type={qs.parse(location.search).type} />
                )}
              />
              <Route
                exact
                path={`${rootPath}/payments/:paymentId`}
                render={({ match }) => (
                  <Payment paymentId={match.params.paymentId} brand={brand} />
                )}
              />

              {/* Standing Orders routes */}
              <Route
                exact
                path={`${rootPath}/recurring-payments/standing-orders/:orderId`}
                render={({ match }) => (
                  <StandingOrder
                    standingOrder={state.getIn([
                      'standingOrders',
                      'index',
                      match.params.orderId,
                    ])}
                    brand={brand}
                  />
                )}
              />

              {/* Recurring payments routes */}
              <Route
                exact
                path={`${rootPath}/recurring-payments/vrp/tpps/:clientId/payments/:consentId`}
                render={({ match }) => (
                  <VrpPayment
                    tpp={state.getIn(['vrp', 'tpps', match.params.clientId])}
                    consent={state.getIn([
                      'vrp',
                      'index',
                      match.params.consentId,
                    ])}
                    onCancelAccess={
                      isStaffMember &&
                      state.getIn([
                        'vrp',
                        'index',
                        match.params.consentId,
                        'status',
                      ]) === 'ACCEPTED'
                        ? null
                        : consentCancelEnabled
                        ? consent => cancelVrp$.next(consent)
                        : null
                    }
                    isStaffMember={isStaffMember}
                    accountNumberFormat={state.getIn([
                      'brand',
                      'accountNumberFormat',
                    ])}
                  />
                )}
              />
              <Route
                exact
                path={`${rootPath}/recurring-payments/vrp/tpps/:clientId/payments`}
                render={({ match }) => {
                  const { clientId } = match.params
                  const tpp = state.getIn(['vrp', 'tpps', clientId])
                  const handle = () => {
                    openTransientModal$.next({
                      type: 'vrp-status-meaning.detail',
                    })
                  }
                  return (
                    <VrpPayments
                      tpp={tpp}
                      handleDisplayStatusesMeaning={handle}
                    />
                  )
                }}
              />
              <Route
                exact
                path={`${rootPath}/recurring-payments/sweeping/tpps/:clientId/payments/:consentId`}
                render={({ match }) => (
                  <SweepingPayment
                    tpp={state.getIn([
                      'sweeping',
                      'tpps',
                      match.params.clientId,
                    ])}
                    consent={state.getIn([
                      'sweeping',
                      'index',
                      match.params.consentId,
                    ])}
                    onCancelAccess={
                      isStaffMember &&
                      state.getIn([
                        'sweeping',
                        'index',
                        match.params.consentId,
                        'status',
                      ]) === 'ACCEPTED'
                        ? null
                        : consentCancelEnabled
                        ? consent => cancelSweeping$.next(consent)
                        : null
                    }
                    isStaffMember={isStaffMember}
                    accountNumberFormat={state.getIn([
                      'brand',
                      'accountNumberFormat',
                    ])}
                  />
                )}
              />
              <Route
                exact
                path={`${rootPath}/recurring-payments/sweeping/tpps/:clientId/payments`}
                render={({ match }) => {
                  const { clientId } = match.params
                  const tpp = state.getIn(['sweeping', 'tpps', clientId])
                  const handle = () => {
                    openTransientModal$.next({
                      type: 'sweeping-status-meaning.detail',
                    })
                  }
                  return (
                    <SweepingPayments
                      tpp={tpp}
                      handleDisplayStatusesMeaning={handle}
                    />
                  )
                }}
              />
              <Route
                path={`${rootPath}/recurring-payments/:paymentType(standing-orders|vrp|sweeping)`}
                render={({ match }) => (
                  <RecurringPayments
                    rootPath={rootPath}
                    paymentType={match.params.paymentType}
                  />
                )}
              />

              {showMortgageConsents && (
                <Route
                  path={`${rootPath}/mortgages`}
                  exact
                  render={() => (
                    <Mortgages mortgages={state.getIn(['mortgages', 'list'])} />
                  )}
                />
              )}

              {/* Staff routes */}
              {isStaffMember && (
                <>
                  <Route
                    path={`${rootPath}/biometrics`}
                    exact
                    render={() => (
                      <Biometrics
                        biometrics={state.getIn(['biometrics', 'list'])}
                      />
                    )}
                  />
                  {isPayMeYouEnabled && (
                    <>
                      <Route path={`${rootPath}/paymeyou`} exact>
                        <Redirect to={`${rootPath}/paymeyou/pay-me`} />
                      </Route>
                      <Route
                        path={`${rootPath}/paymeyou/pay-me/:linkId`}
                        exact
                        render={({
                          match: {
                            // eslint-disable-next-line no-unused-vars
                            params: { linkId },
                          },
                        }) => (
                          <LinkHistory
                            type={'pay-me'}
                            onCancelLink={
                              consentCancelEnabled
                                ? link => cancelLink$.next(link)
                                : null
                            }
                          />
                        )}
                      />
                      <Route
                        path={`${rootPath}/paymeyou/pay-me/reusable-link/:linkId`}
                        exact
                        render={({
                          match: {
                            // eslint-disable-next-line no-unused-vars
                            params: { linkId },
                          },
                        }) => (
                          <LinkHistory
                            type={'pay-me'}
                            onCancelLink={
                              consentCancelEnabled
                                ? link => cancelLink$.next(link)
                                : null
                            }
                          />
                        )}
                      />
                      <Route
                        path={`${rootPath}/paymeyou/pay-you/:linkId`}
                        exact
                        render={({
                          match: {
                            // eslint-disable-next-line no-unused-vars
                            params: { linkId },
                          },
                        }) => (
                          <LinkHistory
                            type={'pay-you'}
                            onCancelLink={
                              consentCancelEnabled
                                ? link => cancelLink$.next(link)
                                : null
                            }
                          />
                        )}
                      />
                      <Route
                        path={`${rootPath}/paymeyou/:type(pay-me|pay-you)`}
                        exact
                        render={({ match }) => (
                          <PayMeYou
                            rootPath={rootPath}
                            type={match.params.type}
                          />
                        )}
                      />
                    </>
                  )}
                </>
              )}

              {/* Misc routes */}
              <Redirect exact from="/privacy" to={`${rootPath}/privacy`} />
              <Route
                path={`${rootPath}/privacy`}
                exact
                render={() => <Privacy />}
              />
              <Route
                render={() => (
                  <Error
                    error={{ type: 'page-not-found' }}
                    brand={brand}
                    isStaffMember={isStaffMember}
                  />
                )}
              />
            </Switch>
          )}
        </main>
        <Footer
          className={style.footer}
          brand={brand}
          isLegacyTheme={isLegacyTheme(brand.get('id'))}
        />
        {state.has('modal') && (
          <Modal modal={state.get('modal')} brand={brand} />
        )}
      </div>
    </Router>
  )
}

export default render$

render$
  .pipe(
    map(state => state.get('redirectTo')),
    filter(Boolean),
    flog('Redirect')
  )
  .subscribe(redirectSink)

render$
  .pipe(
    filter(hasRequiredPropsForRender),
    flog('Render state'),
    map(createStateContext(toView))
  )
  .subscribe(domSink)
